import React, { useEffect, useMemo, useRef, useState } from "react";

const DemoFlutterFrame = ({
  session,
  sessionId,
  markRendered,
  baseUrl = process.env.REACT_APP_FLUTTER_DEMO_URL || "https://etikaf-app-demo.web.app",
  timeoutMs = 15_000,
}) => {
  const iframeRef = useRef(null);
  const shellRef = useRef(null);
  const acceptedBuildRef = useRef("");
  const [frameKey, setFrameKey] = useState(0);
  const [frameState, setFrameState] = useState("loading");
  const expectedOrigin = useMemo(() => new URL(baseUrl).origin, [baseUrl]);
  const src = useMemo(() => {
    const url = new URL("/demo", baseUrl);
    url.searchParams.set("session", sessionId);
    return url.toString();
  }, [baseUrl, sessionId]);
  const expectedRevision = session?.delivery?.revision;
  const expectedContentType = session?.delivery?.contentType;
  const ping = () => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        channel: "etikaf-demo",
        version: 1,
        sessionId,
        type: "demo.ping",
      },
      expectedOrigin
    );
  };

  useEffect(() => {
    acceptedBuildRef.current = "";
  }, [frameKey, sessionId]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    const applyScale = () => {
      shell.style.setProperty("--demo-phone-scale", String(shell.clientWidth / 402));
    };
    applyScale();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", applyScale);
      return () => window.removeEventListener("resize", applyScale);
    }
    const observer = new ResizeObserver(applyScale);
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setFrameState("loading");
    const timeout = window.setTimeout(
      () => setFrameState((current) => current === "loading" ? "timeout" : current),
      timeoutMs
    );
    return () => window.clearTimeout(timeout);
  }, [frameKey, sessionId, timeoutMs]);

  useEffect(() => {
    const receive = (event) => {
      let payload = event.data || {};
      if (typeof payload === "string") {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (
        payload.channel !== "etikaf-demo" ||
        payload.version !== 1 ||
        payload.source !== "etikaf-flutter-demo" ||
        payload.sessionId !== sessionId
      ) return;
      const validBuild = typeof payload.buildId === "string"
        && /^[0-9a-f]{7,40}$/i.test(payload.buildId);
      if (payload.type === "demo.ready" && validBuild) {
        acceptedBuildRef.current = payload.buildId;
        setFrameState("ready");
      }
      if (payload.type === "demo.error") setFrameState("error");
      if (payload.type === "demo.expired") setFrameState("expired");
      if (
        payload.type === "demo.rendered" &&
        Number.isSafeInteger(expectedRevision) &&
        expectedContentType &&
        validBuild &&
        acceptedBuildRef.current === payload.buildId &&
        payload.revision === expectedRevision &&
        payload.contentType === expectedContentType
      ) {
        setFrameState("rendered");
        markRendered("app", expectedRevision, expectedContentType);
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [expectedContentType, expectedOrigin, expectedRevision, markRendered, sessionId]);

  return (
    <section className="demo-preview demo-app-preview" aria-label="Paparan aplikasi jemaah">
      <div className="demo-panel-heading">
        <div><p className="demo-eyebrow">APLIKASI ETIKAF</p><h2>Aplikasi jemaah</h2></div>
        <span className={`demo-frame-status is-${frameState}`} aria-live="polite">
          {frameState === "rendered" ? "Diterima" : frameState === "ready" ? "Sedia" : "Demo"}
        </span>
      </div>
      <div className="demo-phone-shell demo-flutter-shell" ref={shellRef}>
        <iframe
          key={frameKey}
          ref={iframeRef}
          src={src}
          title="Aplikasi Etikaf demo"
          onLoad={ping}
          width="402"
          height="874"
          data-native-viewport="402x874"
        />
      </div>
      {["timeout", "error", "expired"].includes(frameState) && (
        <div className="demo-frame-help" role="status">
          <p>Paparan aplikasi belum dapat disambungkan. Makluman anda masih disimpan.</p>
          <button type="button" onClick={() => setFrameKey((value) => value + 1)}>Cuba semula</button>
        </div>
      )}
    </section>
  );
};

export default DemoFlutterFrame;
